import React from 'react';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useAuth } from '../../auth';
import { exchangeAuthCode } from '../../auth/api';
import { useGlobalError } from '../../common/ErrorHandler';

const EXCHANGE_TIMEOUT_MS = 15_000;
const inFlightCodeExchanges = new Map<string, Promise<Awaited<ReturnType<typeof exchangeWithTimeout>>>>();

async function exchangeWithTimeout(code: string) {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new Error('AUTH_CODE_EXCHANGE_TIMEOUT'));
    }, EXCHANGE_TIMEOUT_MS);
  });

  try {
    return await Promise.race([exchangeAuthCode(code), timeoutPromise]);
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }
}

function getOrStartCodeExchange(code: string) {
  const existing = inFlightCodeExchanges.get(code);
  if (existing) {
    return existing;
  }

  const next = exchangeWithTimeout(code).finally(() => {
    inFlightCodeExchanges.delete(code);
  });
  inFlightCodeExchanges.set(code, next);
  return next;
}

export const AuthCallback = () => {
  const { hydrated, session, login } = useAuth();
  const { setError: setGlobalError } = useGlobalError();
  const [authFailed, setAuthFailed] = React.useState(false);

  const showAuthFailure = React.useCallback(() => {
    setAuthFailed(true);
    setGlobalError({
      title: 'Internal Sign-in Failed',
      message: 'Authentication failed. Close this tab and open the application in a new tab.',
      isAuthError: true,
      reason: 'exchange-failure',
    });
  }, [setGlobalError]);

  React.useEffect(() => {
    let active = true;

    const run = async () => {
      if (!hydrated || authFailed || typeof window === 'undefined') return;

      if (session) {
        if (window.location.pathname !== '/home') {
          window.history.replaceState({}, document.title, '/home');
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (!code) {
        showAuthFailure();
        return;
      }

      try {
        const nextSession = await getOrStartCodeExchange(code);
        if (!active) return;
        login(nextSession);
        window.history.replaceState({}, document.title, '/home');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch {
        if (!active) return;
        showAuthFailure();
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [authFailed, hydrated, login, session, showAuthFailure]);

  if (authFailed) return null;

  return (
    <Layout.Stack
      alignLayout="center"
      css={{
        minHeight: '100vh',
        padding: '24px',
        background: 'radial-gradient(circle at 15% 20%, #d7f2ff 0%, #eef7ff 36%, #f7f9ff 100%)',
      }}
    >
      <div
        style={{
          width: 'min(560px, 94vw)',
          borderRadius: '20px',
          border: '1px solid #c7d6f8',
          background: 'linear-gradient(160deg, #ffffff 0%, #f6f9ff 100%)',
          boxShadow: '0 18px 50px rgba(28, 62, 120, 0.18)',
          padding: '28px 24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            marginBottom: '14px',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #004f91 0%, #00a1d6 100%)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '18px',
              letterSpacing: '0.4px',
            }}
          >
            RFI
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#17356f' }}>Connecting to Entra ID</div>
            <div style={{ fontSize: '14px', color: '#46618f' }}>
              Verifying your sign-in and preparing your workspace.
            </div>
          </div>
        </div>

        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr',
            alignItems: 'center',
            gap: '12px',
            borderRadius: '14px',
            border: '1px solid #d6e2fa',
            background: '#f8fbff',
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              border: '3px solid #c2daf9',
              borderTopColor: '#1e6fc6',
              animation: 'rfi-auth-spin 1s linear infinite',
            }}
          />
          <Text>Completing secure sign-in...</Text>
        </div>

        <style>
          {`@keyframes rfi-auth-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
        </style>
      </div>
    </Layout.Stack>
  );
};
